import { TimingEventAction, TimingEventType } from '@ikegps/core-api';
import { reaction } from 'mobx';
import { type IkeClientService, Services } from './index';

export enum EventCategory {
	Auth = 'Authentication',
	BigMap = 'BigMap',
	Collection = 'Collection',
	Export = 'Export',
	Form = 'Form',
	Import = 'Import',
	Mutate = 'Mutate',
	Performance = 'Performance',
	Route = 'Route',
	Upload = 'Upload',
	FormImportUpload = 'FormImportUpload',
}

export class AnalyticsService implements IkeClientService {
	readonly __priority = 100;

	readonly EventCategory = EventCategory;
	readonly DEBOUNCE_TIME = 1000;

	readonly TimingEventType = TimingEventType;
	readonly TimingEventAction = TimingEventAction;

	init() {
		reaction(
			(_) => Services.User.current,
			(_) => this.setUser(),
			{ fireImmediately: true }
		);
		reaction(
			(_) => Services.Department.current,
			(_) => this.setDepartment(),
			{ fireImmediately: true }
		);

		// Track that the system started
		this.trackEvent(EventCategory.Auth, 'page:init', 'init', 1);
	}

	setUser() {
		if (Services.Window.window.ga == null) {
			return;
		}
		const user = Services.User.current;
		if (user == null) {
			Services.Window.window.ga('set', 'userId', null);
		} else {
			Services.Window.window.ga('set', 'userId', user.objectId);
		}
	}

	setDepartment() {
		if (Services.Window.window.ga == null) {
			return;
		}
		const department = Services.Department.current;
		if (department == null) {
			Services.Window.window.ga('set', 'dimension1', null);
		} else {
			Services.Window.window.ga('set', 'dimension1', department.objectId);
		}
	}

	trackTiming(type: TimingEventType, action: TimingEventAction, collectionId: string, recordedAt: Date) {
		if (Services.User.hasUser) {
			const data = {
				events: [
					{
						type,
						action,
						collectionId,
						recordedAt: recordedAt.toISOString(),
					},
				],
			};

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			Services.Department.rest.Event.create(data);
		}
	}

	/**
	 * Track event.
	 *
	 * @param category Typically the object that was interacted with (e.g. 'Collection')
	 * @param action The type of interaction (e.g. 'brightness:increase')
	 * @param label Useful for categorizing events (e.g. Id of object affected)
	 * @param value A numeric value associated with the event (e.g. 42)
	 */
	trackEvent(category: EventCategory, action: string, label?: string, value?: number) {
		if (Services.Window.window.ga == null) {
			return;
		}
		Services.Window.window.ga('send', 'event', category, action, label, value);
	}
}
